import { z } from 'zod';
import { CASAS_PERCENTUAL, desescalar } from './aritmetica.ts';
import {
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  listagemDeCadastroSchema,
  respostaListaSchema,
  tipoContratoSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Contratos (`.spec/010`, módulo Contratos; `.spec/016`, seção 4).
 *
 * O contrato é o que amarra a contraparte à obra: `contratos` agrupa `obras`, e
 * a obra é o centro de custo de todo rateio do sistema. Desde o financeiro do
 * contrato ele é também **documento que gera título** — a receber ou a pagar,
 * conforme o tipo.
 *
 * ## O tipo decide quem é a contraparte
 *
 * É a decisão que reorganiza este arquivo. `pessoaId` é cliente no
 * `RECEBIMENTO` e fornecedor no `PAGAMENTO`, e por isso o campo `tipo` sobe para
 * a Identificação, **antes** do campo de pessoa: escolher a pessoa primeiro
 * obrigaria a tela a listar quem pode ser as duas coisas, ou a limpar a escolha
 * quando o tipo mudasse depois.
 *
 * A alternativa descartada era exigir que a mesma pessoa carregasse as duas
 * flags. Ela obrigaria a marcar como cliente a empreiteira que só recebe — e a
 * flag `is_cliente` é o que faz alguém aparecer no seletor de cliente de todo o
 * resto do sistema.
 *
 * ## O que o ato exige, o ato confere
 *
 * `empresaId` é **sempre** obrigatória: o título exige empresa, e perguntá-la no
 * ato de gerar faria a segunda geração poder responder diferente da primeira.
 * Ela é a exceção, e é a única — de quem o contrato é tem resposta na assinatura.
 *
 * `valor`, `contaContabilId` e `condicaoPagamentoId` são **as três opcionais no
 * cadastro**, e nenhuma condiciona a outra. É a regra que o módulo aprendeu:
 * *o que o ato exige, o ato confere*. Gerar parcelas precisa das três; **salvar
 * o contrato não precisa de nenhuma.**
 *
 * A alternativa — exigi-las quando há valor — foi tentada e desfeita. Ela
 * assumia que quem fecha o valor já decidiu classificação e prazo, e isso não é
 * verdade: contrato se assina com o valor negociado e o resto em aberto, e a
 * exigência só produzia escolha feita por obrigação, para conseguir salvar. Uma
 * conta contábil escolhida assim é a que joga o contrato inteiro no ramo errado
 * do DRE, e uma condição escolhida assim é a que gera doze vencimentos que
 * ninguém confere.
 *
 * Quem cobra as três é o service do financeiro, com nome próprio para cada
 * ausência (`contrato_sem_valor`, `contrato_sem_conta_contabil`,
 * `contrato_sem_condicao`) e com o cadastro a um clique de distância. E ele as
 * relê do banco de qualquer forma — cadastro é editável, e a conta correta na
 * assinatura pode ter virado sintética meses depois.
 */

/* -------------------------------------------------------------------------- */
/* Tipo do contrato                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Recebimento ou pagamento.
 *
 * O `z.enum` mora em `comum.ts`, junto com os outros códigos estáveis do
 * domínio, e aqui só se derivam a lista e o tipo. Redeclará-lo daria dois
 * lugares para acrescentar um código novo — e o esquecido seria justamente o
 * que a coluna `CHECK contratos_tipo_valido` valida.
 */
export const TIPOS_CONTRATO = tipoContratoSchema.options;
export type TipoContrato = z.infer<typeof tipoContratoSchema>;

/** Rótulos de tela. Ficam no contrato para API e web nunca escreverem dois nomes. */
export const ROTULO_TIPO_CONTRATO: Readonly<Record<TipoContrato, string>> = {
  RECEBIMENTO: 'Recebimento',
  PAGAMENTO: 'Pagamento',
};

/**
 * O que cada tipo implica, para a dica do formulário.
 *
 * "Recebimento" e "pagamento" são ambíguos sozinhos — dependem de quem fala.
 * A frase diz de que lado a construtora está, que é a única coisa que o usuário
 * precisa decidir no campo.
 */
export const DICA_TIPO_CONTRATO: Readonly<Record<TipoContrato, string>> = {
  RECEBIMENTO: 'A construtora executa e recebe do cliente. É o caso comum da obra contratada.',
  PAGAMENTO: 'A construtora contrata e paga — obra tocada por terceiro, empreitada global.',
};

/**
 * Comprimento máximo do número.
 *
 * É texto, não inteiro: número de contrato carrega ano, sigla e barra
 * ("2026/014-ADM"), e guardá-lo como número perderia o zero à esquerda que o
 * documento tem impresso.
 */
export const TAMANHO_MAXIMO_NUMERO_CONTRATO = 60;

/**
 * O papel que a contraparte precisa ter, por tipo de contrato.
 *
 * Fica aqui, e não no service, porque a **tela** decide qual seletor abrir a
 * partir da mesma tabela. Duas cópias divergiriam no dia em que um terceiro tipo
 * aparecesse, e a divergência seria um formulário oferecendo fornecedores para
 * um contrato que o servidor recusa.
 */
export const PAPEL_DA_CONTRAPARTE = {
  RECEBIMENTO: 'CLIENTE',
  PAGAMENTO: 'FORNECEDOR',
} as const satisfies Readonly<Record<TipoContrato, 'CLIENTE' | 'FORNECEDOR'>>;

export type PapelDaContraparte = (typeof PAPEL_DA_CONTRAPARTE)[TipoContrato];

/** Rótulo do campo de pessoa. Muda com o tipo — ver `PAPEL_DA_CONTRAPARTE`. */
export const ROTULO_CONTRAPARTE: Readonly<Record<TipoContrato, string>> = {
  RECEBIMENTO: 'Cliente',
  PAGAMENTO: 'Fornecedor',
};

/**
 * Os grupos do plano de contas que cada tipo aceita (`.spec/010`).
 *
 * Receita no que entra; custo ou despesa no que sai. São **dois** grupos do lado
 * do pagamento porque a empreitada da obra é custo e o contrato de serviço
 * administrativo é despesa, e o plano de contas já separa os dois — escolher um
 * só aqui jogaria metade dos contratos na conta errada do DRE.
 */
export const GRUPOS_CONTA_DO_CONTRATO = {
  RECEBIMENTO: ['RECEITA'],
  PAGAMENTO: ['CUSTO', 'DESPESA'],
} as const satisfies Readonly<Record<TipoContrato, readonly string[]>>;

/* -------------------------------------------------------------------------- */
/* Cadastro                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Valor global do contrato: **positivo**, e opcional.
 *
 * Opcional porque contrato por medição não tem valor fechado na assinatura —
 * exigi-lo faria o usuário digitar uma estimativa, e estimativa gravada não se
 * distingue de valor contratado depois.
 *
 * Positivo porque o sentido do dinheiro é o `tipo`, nunca o sinal do número.
 * Um "recebimento de −100" e um "pagamento de 100" diriam a mesma coisa de duas
 * formas, e o primeiro relatório que somasse os dois teria de escolher uma.
 */
const valorDoContratoSchema = dinheiroSchema.refine(
  (v) => Number(v) > 0,
  'O valor do contrato deve ser maior que zero.',
);

/**
 * Fim de vigência nunca antes do início.
 *
 * Mesma regra do `CHECK contratos_vigencia_coerente`, e a repetição é
 * deliberada: o `CHECK` recusa a linha com uma violação de constraint que o
 * usuário não consegue interpretar; aqui o erro chega preso ao campo, e o
 * formulário sabe qual destacar.
 *
 * A comparação é de texto, e é correta porque `AAAA-MM-DD` ordena
 * lexicograficamente igual ao calendário. Converter para `Date` traria fuso
 * para dentro de uma data de negócio — e vigência é o dia, não um instante.
 */
function validarVigencia(
  dados: { dataInicioVigencia: string | null; dataFimVigencia: string | null },
  ctx: z.RefinementCtx,
): void {
  const { dataInicioVigencia: inicio, dataFimVigencia: fim } = dados;
  if (inicio === null || fim === null) return;
  if (fim >= inicio) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['dataFimVigencia'],
    message: 'O fim da vigência não pode ser anterior ao início.',
  });
}

/*
  **Não há `validarFinanceiro`.**

  Existiu, exigindo conta contábil e condição de pagamento quando havia `valor`,
  e foi removido — ver o comentário do módulo. O bloco fica registrado aqui como
  ausência deliberada: a próxima pessoa que ler `contaContabilId` opcional ao
  lado de um `valor` preenchido vai querer "consertar" isso, e o conserto
  devolveria a escolha feita por obrigação que a regra existe para evitar.

  A vigência continua sendo conferida aqui (`validarVigencia`), e a diferença é
  o tipo de regra: vigência é **coerência entre dois campos preenchidos**, que
  não depende de ato nenhum. Obrigatoriedade de campo para gerar parcela depende
  — e quem depende do ato pertence ao ato.
*/

export const criarContratoSchema = z
  .object({
    /**
     * Número do documento. Único **na conta**, não globalmente.
     *
     * É por ele que o contrato se procura — quem liga perguntando cita o
     * número, não a descrição.
     */
    numero: z
      .string()
      .trim()
      .min(1, 'Informe o número do contrato.')
      .max(
        TAMANHO_MAXIMO_NUMERO_CONTRATO,
        `O número tem no máximo ${TAMANHO_MAXIMO_NUMERO_CONTRATO} caracteres.`,
      ),
    /** Data de assinatura. Data de negócio, sem hora. */
    dataContrato: dataSchema,
    /**
     * Vigência e prazo, os três **opcionais**.
     *
     * Contrato assinado sem data de início definida existe, e é comum: a
     * vigência começa com a ordem de serviço, que vem depois. Exigir as três
     * datas na assinatura produziria palpite gravado como fato.
     */
    dataInicioVigencia: dataSchema.nullable().prefault(null),
    dataFimVigencia: dataSchema.nullable().prefault(null),
    dataPrazoConclusao: dataSchema.nullable().prefault(null),
    /**
     * A contraparte. Cliente no recebimento, fornecedor no pagamento — ver
     * `PAPEL_DA_CONTRAPARTE`. A API recusa quem não tiver a flag do papel.
     */
    pessoaId: uuidSchema,
    /**
     * Empresa do grupo que assina — `pessoas` com `is_empresa`.
     *
     * Obrigatória mesmo no contrato por medição, que não gera título nenhum: ela
     * é de quem o contrato é, e essa pergunta tem resposta desde a assinatura.
     */
    empresaId: uuidSchema,
    /** Objeto do contrato, em texto livre. */
    descricao: z
      .string()
      .trim()
      .min(2, 'Descreva o objeto do contrato.')
      .max(2000, 'A descrição tem no máximo 2000 caracteres.'),
    valor: valorDoContratoSchema.nullable().prefault(null),
    tipo: tipoContratoSchema,
    /**
     * Natureza do dinheiro do contrato. **Opcional aqui** — nem o valor a torna
     * obrigatória.
     *
     * O grupo tem de ser coerente com o tipo (`GRUPOS_CONTA_DO_CONTRATO`), e a
     * conta tem de ser folha do plano. Quem cobra as duas coisas — e a própria
     * presença dela — é a geração das parcelas: o grupo e as filhas moram em
     * `contas_contabeis`, e o contrato Zod não alcança outra tabela.
     */
    contaContabilId: uuidSchema.nullable().prefault(null),
    /**
     * Quem dá as parcelas. **Opcional aqui**, como a conta contábil.
     *
     * Contrato assinado com valor fechado e prazo ainda em negociação existe, e
     * exigi-la na assinatura faria alguém escolher uma condição qualquer só para
     * conseguir salvar. Quem a cobra é a geração, que é o único ato que precisa
     * dela.
     */
    condicaoPagamentoId: uuidSchema.nullable().prefault(null),
    ativo: z.boolean().prefault(true),
  })
  .superRefine(validarVigencia);

export type CriarContrato = z.infer<typeof criarContratoSchema>;
export const atualizarContratoSchema = criarContratoSchema;
export type AtualizarContrato = z.infer<typeof atualizarContratoSchema>;

export const contratoSchema = z.object({
  id: uuidSchema,
  numero: z.string(),
  dataContrato: dataSchema,
  dataInicioVigencia: dataSchema.nullable(),
  dataFimVigencia: dataSchema.nullable(),
  dataPrazoConclusao: dataSchema.nullable(),
  pessoaId: uuidSchema,
  /**
   * Nome da contraparte, desnormalizado na leitura.
   *
   * A listagem mostra de quem é o contrato sem uma segunda consulta por linha —
   * que, numa página de 25, seriam 25 idas ao banco para exibir uma coluna.
   */
  pessoaNome: z.string().nullable(),
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  /**
   * O orçamento de que este contrato nasceu (`.spec/015`, decisão 59).
   *
   * Nulo no contrato lançado à mão, que é o caso da maioria dos existentes. O id
   * e o número vêm juntos pelo motivo de sempre: a tela mostra "Orçamento 87", e
   * resolvê-lo no cliente seria uma consulta por linha.
   */
  orcamentoId: uuidSchema.nullable(),
  orcamentoNumero: z.number().int().nullable(),
  descricao: z.string(),
  /** String decimal, nunca number: ponto flutuante em dinheiro perde centavos. */
  valor: dinheiroSchema.nullable(),
  tipo: tipoContratoSchema,
  contaContabilId: uuidSchema.nullable(),
  contaContabilCodigo: z.string().nullable(),
  contaContabilDescricao: z.string().nullable(),
  condicaoPagamentoId: uuidSchema.nullable(),
  condicaoPagamentoDescricao: z.string().nullable(),
  /**
   * Quantos títulos o contrato já gerou. **Derivado**, nunca coluna.
   *
   * É o que abre e fecha o botão de gerar: zero, o botão aparece; mais que zero,
   * o financeiro está em dia. Uma coluna `financeiro_desatualizado` diria a mesma
   * coisa e poderia discordar dos títulos que existem — é a mesma escolha que
   * deixou `situacao` fora do título.
   */
  qtdTitulosGerados: z.number().int().min(0),
  /**
   * Algum título gerado já recebeu baixa.
   *
   * Sai daqui, e não de uma consulta separada da tela, porque é ele que decide
   * se o formulário pode oferecer a edição de valor, tipo e condição — e a tela
   * precisa saber disso **antes** de o usuário digitar, não depois de o `PUT`
   * recusar.
   */
  temTituloBaixado: z.boolean(),
  /**
   * Quantos **aditivos** foram lançados contra este contrato, e quanto somam.
   *
   * Aditivo é título a receber digitado apontando para o contrato — o caminho
   * que resta depois que o financeiro foi movimentado e as parcelas não se
   * refazem mais. Não é parcela: não conta para `qtdTitulosGerados`, não abre
   * nem fecha o botão de gerar, e a edição do contrato não o apaga.
   *
   * Está aqui, e não numa consulta da tela, pelo mesmo motivo de
   * `temTituloBaixado`: sem esses dois números o cadastro diria "3 títulos
   * gerados" sobre um contrato que já recebeu mais do que isso, e quem conferir
   * o total vai concluir que faltou lançamento.
   *
   * O valor é a soma de `valor`, não do saldo: a pergunta é quanto foi aditado,
   * e o aditivo já recebido continua tendo aditado o contrato. Contrato sem
   * aditivo devolve `'0'`.
   */
  qtdAditivos: z.number().int().min(0),
  valorAditivos: dinheiroSchema,
  /**
   * Quanto as notas de serviço já faturaram por este contrato (`.spec/015`,
   * decisão 96): as que o citam **e** as que apontam uma obra dele, cada nota
   * uma vez, pelo serviço menos o desconto incondicionado.
   *
   * Vem na leitura por causa da listagem, como em `obraSchema`; o saldo é
   * `saldoAFaturar(valor, faturadoEmNotas)`. Só tem sentido no contrato de
   * **recebimento** — no de pagamento é sempre `'0.00'`, e a tela não o mostra.
   * Não é aditivo, que é título digitado: são coisas diferentes, e somam.
   *
   * **`null` para quem não vê o saldo a faturar** (decisões 97 e 100) — ver o campo de
   * mesmo nome em `obraSchema`.
   */
  faturadoEmNotas: dinheiroSchema.nullable(),
  ativo: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type Contrato = z.infer<typeof contratoSchema>;

/**
 * Os campos de que os títulos gerados descendem — os que a **baixa congela**.
 *
 * Alterar qualquer um deles depois de alguém ter recebido ou pago uma parcela
 * deixaria a baixa apontando para um contrato que já não a explica; por isso o
 * `PUT` recusa quando `temTituloBaixado`. Sem baixa, mudar é permitido: a edição
 * apaga os títulos e o botão de gerar reabre por consequência — não há flag de
 * "desatualizado" a manter em dia.
 *
 * A lista vive aqui, e não só no service, porque é ela que a tela usa para
 * decidir quais campos desabilitar. Uma tela que deixasse digitar o que o
 * servidor recusa seria pior do que uma que não deixa.
 */
export const CAMPOS_CONGELADOS_POR_BAIXA = ['valor', 'tipo', 'condicaoPagamentoId'] as const;

export const listaContratosSchema = respostaListaSchema(contratoSchema);
export type ListaContratos = z.infer<typeof listaContratosSchema>;

/**
 * Listagem, com três filtros além dos comuns.
 *
 * `tipo` porque quem confere recebimento não quer ver empreitada paga,
 * `pessoaId` porque a pergunta frequente é "quais contratos são deste cliente?",
 * e `empresaId` porque o grupo com mais de uma construtora confere uma de cada
 * vez. Nenhum dos três se resolve pelo campo de busca: o número do contrato não
 * carrega nem o sentido nem o nome de quem assinou.
 */
export const listarContratosQuerySchema = listagemDeCadastroSchema.extend({
  tipo: tipoContratoSchema.optional(),
  pessoaId: uuidSchema.optional(),
  empresaId: uuidSchema.optional(),
});

export type ListarContratosQuery = z.infer<typeof listarContratosQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Geração do financeiro                                                      */
/* -------------------------------------------------------------------------- */

/**
 * O que o botão de gerar manda: **uma data**, e só.
 *
 * A data base é de onde a condição de pagamento conta os dias — "30/60/90" a
 * partir dela. Ela **não é campo gravado do contrato**, e a distinção é a decisão
 * deste schema: gravá-la faria o cadastro carregar um dado que só serve ao
 * instante da geração, e que ficaria mentindo no dia em que os títulos fossem
 * apagados e refeitos a partir de outro dia.
 *
 * A tela sugere a data do contrato, que é o caso comum; quem gera meses depois
 * a troca sem que o cadastro registre nada.
 *
 * Nada mais entra no payload — nem valor, nem condição, nem obras. Todos são do
 * contrato, e aceitá-los aqui abriria a porta para gerar títulos que não
 * correspondem ao documento que os originou.
 */
export const gerarFinanceiroDoContratoSchema = z.object({
  dataBase: dataSchema,
});

export type GerarFinanceiroDoContrato = z.infer<typeof gerarFinanceiroDoContratoSchema>;

/**
 * Uma parcela como a geração a devolve — o resumo que a tela mostra.
 *
 * É a resposta do `POST`, e não a lista de títulos com todos os campos deles:
 * quem acabou de gerar quer conferir vencimento e valor, e os títulos inteiros
 * estão nas telas de contas a receber e a pagar, a um clique de distância.
 */
export const parcelaDoContratoSchema = z.object({
  parcela: z.number().int().min(1),
  vencimento: dataSchema,
  valor: dinheiroSchema,
  /** Id do título gerado — a receber ou a pagar, conforme o tipo do contrato. */
  tituloId: uuidSchema,
});

export type ParcelaDoContrato = z.infer<typeof parcelaDoContratoSchema>;

export const financeiroDoContratoSchema = z.object({
  contrato: contratoSchema,
  parcelas: z.array(parcelaDoContratoSchema),
});

export type FinanceiroDoContrato = z.infer<typeof financeiroDoContratoSchema>;

/**
 * Rateio dos títulos pelas **obras do contrato**, em partes iguais.
 *
 * A regra tem uma frase: uma obra leva 100%; várias dividem igualmente. Não há
 * peso a aplicar — o contrato não diz quanto de cada obra ele vale, e inventar
 * um critério (área, valor de orçamento) seria decidir custo de obra a partir de
 * um dado que ninguém informou.
 *
 * A sobra da divisão vai para a **primeira** obra, e não é detalhe: três obras
 * dão 33,3333% cada e somam 99,9999%, e os 0,0001% que faltam apareceriam como
 * receita sem obra num relatório que ninguém sabe explicar. A primeira é a de id
 * menor — `uuidv7`, portanto a mais antiga —, o que torna o resultado o mesmo em
 * toda regeração.
 *
 * Contrato **sem obra** devolve lista vazia, e a função continua sendo a mesma —
 * o título nasce sem rateio, como o da compra de almoxarifado central. Quem
 * decide se isso é aceitável é a geração, e ela responde diferente para cada
 * tipo: o contrato de **recebimento** sem obra é recusado (`contrato_sem_obra`),
 * porque faturamento fora de obra some do resultado dela; o de **pagamento**
 * passa, porque aluguel da sede e seguro são despesa administrativa que não
 * pertence a obra alguma. Ver `rateioDeObraCabe`, em `titulos.ts`.
 */
export function rateioIgualitarioDeObras(
  obraIds: readonly string[],
): { obraId: string; indiceRateio: string }[] {
  if (obraIds.length === 0) return [];

  const ordenadas = [...obraIds].sort();
  // Em milésimos de ponto percentual: 100% são 1.000.000 unidades de 0,0001%.
  const TOTAL = 1_000_000n;
  const quantas = BigInt(ordenadas.length);
  const fatia = TOTAL / quantas;
  const sobra = TOTAL - fatia * quantas;

  return ordenadas.map((obraId, posicao) => ({
    obraId,
    indiceRateio: desescalar(posicao === 0 ? fatia + sobra : fatia, CASAS_PERCENTUAL),
  }));
}

/**
 * Colunas de ordenação da listagem.
 *
 * A ordem padrão é `-dataContrato` — o contrato mais recente primeiro. Ordem
 * alfabética por número seria correta e inútil: quem abre a tela quer ver o que
 * foi assinado por último, não o contrato 001 de três anos atrás.
 */
export const COLUNAS_ORDENAVEIS_CONTRATO = [
  'numero',
  'dataContrato',
  'valor',
  'createdAt',
] as const;
